import { Link, useParams } from "react-router-dom";
import { Navigation } from "lucide-react";

import "../Styles/Card.css";
import "../Styles/VenuePage.css";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import VenueCheckIn from "../Components/VenueCheckIn";
import VenueNowPlaying from "../Components/Venue/VenueNowPlaying";
import VenueSummary from "../Components/Venue/VenueSummary";
import { useVenue } from "../Hooks/useVenue";
import { ApiError } from "../Services/http";
import { clockTime } from "../utils/format";
import { directionsUrl } from "../utils/directions";
import { musicStatus } from "../utils/venueMusic";
import type { MapFeedItemDTO } from "../data/types";

/**
 * Ett ställe som egen sida, /venue/:id.
 *
 * Läser GET /venues/:id och inte kartflödet, så ett ställe som kartan inte
 * visar just nu — ingen koordinat, bortom flödets gräns — ändå går att
 * öppna. Själva närvaron (vilka som är där, check-in, på väg) är samma
 * komponent som på kartkortet och pratar med samma endpoints.
 */
export default function VenuePage() {
  const { venueId } = useParams();
  const { state, retry } = useVenue(venueId);

  return (
    <>
      <Header />

      <main className="venue-page">
        <Link to="/" className="venue-page-back">
          ‹ Back to the map
        </Link>

        {state.status === "loading" && (
          <p className="venue-page-status" role="status">
            Loading venue…
          </p>
        )}

        {state.status === "notFound" && (
          <div className="venue-page-status" role="alert">
            <p className="venue-page-status-title">Venue not found</p>
            <p>This venue doesn&apos;t exist, or it has been removed.</p>
          </div>
        )}

        {state.status === "error" && (
          <div className="venue-page-status" role="alert">
            <p className="venue-page-status-title">
              {state.error instanceof ApiError && state.error.code === "network_error"
                ? "Can't reach the server"
                : "Could not load the venue"}
            </p>
            <button type="button" className="venue-page-action" onClick={retry}>
              Try again
            </button>
          </div>
        )}

        {state.status === "found" && (
          <VenueDetails item={state.item} stale={state.stale} onRetry={retry} />
        )}
      </main>

      <Footer />
    </>
  );
}

function VenueDetails({
  item,
  stale,
  onRetry,
}: {
  item: MapFeedItemDTO;
  stale: boolean;
  onRetry: () => void;
}) {
  const noLiveData = musicStatus(item).kind === "noLiveData";
  const directions = directionsUrl(item.venue);
  const earlier = item.previouslyPlayed ?? [];

  return (
    <div className="venue-page-body">
      {stale && (
        <div className="venue-page-stale" role="status">
          <span>Can&apos;t reach the server — showing the last known state.</span>
          <button type="button" onClick={onRetry}>
            Try again
          </button>
        </div>
      )}

      <div className={`card-wrapper card-venue${noLiveData ? " card-venue--no-live" : ""}`}>
        <VenueSummary item={item} showNoLiveDataStatus />
        <VenueNowPlaying item={item} />

        {directions && (
          <a
            className="venue-page-action venue-page-directions"
            href={directions}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Navigation size={15} strokeWidth={2.2} />
            Directions
          </a>
        )}

        <VenueCheckIn venueId={item.venue.id} venueName={item.venue.name} />
      </div>

      {!noLiveData && (
        <section className="venue-page-section">
          <h2>Played earlier</h2>

          {earlier.length === 0 ? (
            <p className="venue-page-empty">Nothing played yet.</p>
          ) : (
            <ul className="venue-page-history">
              {earlier.map((song, index) => (
                <li key={`${song.id}-${index}`}>
                  {song.albumCover ? (
                    <img src={song.albumCover} alt="" />
                  ) : (
                    <span className="venue-page-history-cover" aria-hidden="true" />
                  )}

                  <div>
                    <strong>{song.title}</strong>
                    <small>{song.artist}</small>
                  </div>

                  <time dateTime={song.playedAt}>{clockTime(song.playedAt)}</time>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}
